import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  formControlChanges$,
  NgDialogModule,
  SignatureInputComponent,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  IMohFormDisplay,
  IPatient,
  IPractice,
  MohFormDisplayLabel,
  MohEnrolmentSignatureType,
  IMohSendEnrolmentFormRequest,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';
import { MohFormViewerComponent } from '../../moh-form-viewer/moh-form-viewer.component';
import { Validators } from '@angular/forms';
import {
  MohEnrolmentPatientSignatureDialogComponent,
  IMohEnrolmentPatientSignatureData,
} from '../moh-enrolment-patient-signature-dialog/moh-enrolment-patient-signature-dialog.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IMohSignEnrolmentFormDialogData {
  formPath: string;
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
}

export type MohSignEnrolmentFormForm = Pick<
  IMohSendEnrolmentFormRequest,
  | 'agreementHolderSignature'
  | 'patientOrGuardianSignature'
  | 'patientOrGuardianSignatureType'
>;

@Component({
  selector: 'pr-moh-sign-enrolment-form-dialog',
  imports: [
    NgDialogModule,
    MohFormViewerComponent,
    InformationBoxComponent,
    SignatureInputComponent,
  ],
  templateUrl: './moh-sign-enrolment-form-dialog.component.html',
  styleUrl: './moh-sign-enrolment-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MohSignEnrolmentFormDialogComponent {
  private _dialog = inject(MatDialog);
  private _dialogRef =
    inject<
      MatDialogRef<
        MohSignEnrolmentFormDialogComponent,
        MohSignEnrolmentFormForm
      >
    >(MatDialogRef);
  data = inject<IMohSignEnrolmentFormDialogData>(MAT_DIALOG_DATA);
  hasPatientSignature$: Observable<boolean>;

  formDisplay: IMohFormDisplay = {
    formPath: this.data.formPath,
    label: MohFormDisplayLabel.PatientEnrolment,
  };
  form = new TypedFormGroup<MohSignEnrolmentFormForm>({
    agreementHolderSignature: new TypedFormControl<string>(
      '',
      Validators.required
    ),
    patientOrGuardianSignature: new TypedFormControl<string>(''),
    patientOrGuardianSignatureType:
      new TypedFormControl<MohEnrolmentSignatureType>(
        MohEnrolmentSignatureType.NotProvided
      ),
  });

  constructor() {
    this.hasPatientSignature$ = formControlChanges$(
      this.form.controls.patientOrGuardianSignature
    ).pipe(map(Boolean));
  }

  async getPatientSignature(): Promise<void> {
    const signatures = await this._dialog
      .open<
        MohEnrolmentPatientSignatureDialogComponent,
        WithRef<IPatient>,
        IMohEnrolmentPatientSignatureData
      >(
        MohEnrolmentPatientSignatureDialogComponent,
        DialogPresets.medium({ data: this.data.patient })
      )
      .afterClosed()
      .toPromise();
    if (!signatures?.drawnSignature && !signatures?.typedSignature) {
      return;
    }

    this.form.controls.patientOrGuardianSignatureType.setValue(
      signatures.drawnSignature
        ? MohEnrolmentSignatureType.Drawn
        : MohEnrolmentSignatureType.Typed
    );
    this.form.controls.patientOrGuardianSignature.setValue(
      signatures.drawnSignature || signatures.typedSignature
    );
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }
    this._dialogRef.close(this.form.getRawValue());
  }
}
